import React, {useEffect, useState} from 'react';
import { useLocation } from "react-router-dom";
import styles from '../Register/Register.module.css';

import {
  Box,
  Typography,
  Grid,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Input
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import backgroundImage from "../../resources/Login/login-image.png";
import {sendAccountDeletedFeedback} from "../../services/HttpService";
import {deleteAccountReasonOptions} from "../../services/Types";
import {EmailPattern} from "../../services/Util";
import SpinnerModal from "../../components/Modal/Spinner";

const AccountDeleted: React.FC = () => {
  const location = useLocation();
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [reasons, setReasons] = useState<string[]>([]);
  const [otherReason, setOtherReason] = useState<string>('');
  const [isBannerOpen, setIsBannerOpen] = useState<boolean>(false);
  const [backendErrorMessage, setBackendErrorMessage] = useState('');
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailParam = searchParams.get('email');
    if (emailParam && EmailPattern.test(decodeURIComponent(emailParam))) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, [location]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      setShowSpinner(true);
      await sendAccountDeletedFeedback(email, reasons, otherReason);
      setShowSpinner(false);
      setIsSubmitted(true);
      setTimeout(() => {
        window.location.href = 'https://www.sparx.org.nz/';
      }, 5000);
    } catch (error: any) {
      setShowSpinner(false);
      setBackendErrorMessage(error.message);
      setIsBannerOpen(true);

      // Scroll to an element with the ID 'AccountDeletedTitle'
      const targetElement = document.getElementById('AccountDeletedTitle');
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } else {
        console.error('Element not found');
      }
    }
  };

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedReason = event.target.name;
    if (event.target.checked) {
      setReasons((prevReasons) => [...prevReasons, selectedReason]);
    } else {
      setReasons((prevReasons) => prevReasons.filter(reason => reason !== selectedReason));
    }
  };

  return (
    <div className={styles.Register} data-testid="account">
      <SpinnerModal open={showSpinner} />

      <section id="AccountDeletedTitle" className={styles.RegistrationTitle}>
        <Typography variant="h1">Account Deleted</Typography>
      </section>

      {/* Banner for showing backend errors */}
      {isBannerOpen && (
        <div className={styles.RegisterBanner}>
          <div dangerouslySetInnerHTML={{ __html: backendErrorMessage }} />
          <CloseIcon
            onClick={() => setIsBannerOpen(false)}
            style={{ cursor: "pointer" }}
          />
        </div>
      )}

      <Grid container>
        {/* Left Side - Form */}
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: 4,
          }}
        >
          {
            isSubmitted ? <Box sx={{width: "96%", maxWidth: 648, height: 600,
                "@media (min-width: 900px)": {
                  width: "80%",
                },
              }}>
                <Typography variant="h2" sx={{ textAlign: "left", color: "#333333", fontSize: 30 }}>
                  Thank you for sending us your feedback.
                </Typography>
                <Typography variant="body1" component="div">
                  <br/>
                  <span>You’ll automatically be redirected to SPARX Home.</span>
                  <br/>
                </Typography>
              </Box> :
              <Box
                component="form"
                noValidate
                onSubmit={handleSubmit}
                sx={{
                  width: "96%",
                  maxWidth: 648,
                  "@media (min-width: 900px)": {
                    width: "80%",
                  },
                }}
              >
                {/* Email Field */}
                {(!email) && (
                  <Box sx={{ marginBottom: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ textAlign: 'left' }}>Email</Typography>
                    <Input name="email" fullWidth disableUnderline placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)}/>
                  </Box>
                )}

                <FormControl component="fieldset" sx={{ marginBottom: 2 }}>
                  <Typography variant="h2" sx={{ textAlign: "left", color: "#333333", fontSize: 30 }}>Your account has been deleted</Typography>
                  <Typography variant="body1" component="div">
                    <br/>
                    <span>We’re sorry to see you go. Hopefully SPARX has helped.</span>
                    <br/><br/>
                    <span>If you have a moment, please let us know why you deleted your account:</span>
                    <br/>
                  </Typography>
                  <FormGroup>
                    {deleteAccountReasonOptions.map((option, index) => (
                      <FormControlLabel
                        key={index}
                        control={
                          <Checkbox
                            checked={reasons.includes(option.value)}
                            onChange={handleReasonChange}
                            name={option.value}
                          />
                        }
                        label={option.label}
                      />
                    ))}
                  </FormGroup>

                  {reasons.includes('other') && (
                    <>
                      <TextField autoFocus fullWidth
                                 placeholder="In a few words, tell us more"
                                 multiline minRows={3} maxRows={5}
                                 value={otherReason} onChange={(e) => setOtherReason(e.target.value)}
                                 variant="standard"
                                 InputProps={{disableUnderline: true}}
                                 sx={{
                                   marginTop: 2,
                                   width: "100%",
                                   "& .MuiInputBase-root": {
                                     padding: "12px 15px",
                                     height: "auto"
                                   },
                                 }}
                      />
                    </>
                  )}

                </FormControl>

                <button type="submit" className={`PrimaryButton ${styles.RegisterButton}`}>Submit feedback</button>
              </Box>
          }
        </Grid>

        {/* Right Side - Image */}
        <Grid
          item
          xs={false}
          md={5}
          sx={{
            width: "40%",
            height: "auto",
            backgroundImage: `url(${backgroundImage})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            backgroundPositionX: "right",
          }}
        />
      </Grid>
    </div>
  );
};

export default AccountDeleted;
